import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Key, User } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../api/adminApi';
import { useAdminAuth } from '../hooks/useAdminAuth';

const inputClass = "w-full bg-[#1A1A1A] border border-[rgba(212,175,55,0.2)] rounded-sm px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition-colors";
const labelClass = "block text-xs uppercase tracking-wider text-white/50 mb-1.5";

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'editor' });
  const [changePwd, setChangePwd] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const { getUser } = useAdminAuth();
  const currentUser = getUser();

  const fetchUsers = () => {
    setLoading(true);
    adminApi.get('/admin/users').then(r => setUsers(r.data.users || [])).catch(() => toast.error('Failed to load users')).finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post('/admin/users', newUser);
      toast.success(`User "${newUser.username}" created`);
      setShowAdd(false);
      setNewUser({ username: '', password: '', role: 'editor' });
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Create failed'); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.username}"?`)) return;
    try {
      await adminApi.delete(`/admin/users/${user.id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await adminApi.put(`/admin/users/${changePwd.id}/password`, { new_password: newPwd });
      toast.success('Password changed');
      setChangePwd(null);
      setNewPwd('');
    } catch { toast.error('Failed to change password'); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Never';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Admin Users</h1>
          <p className="text-white/40 text-sm mt-0.5">Manage who can access this admin panel</p>
        </div>
        <button onClick={() => setShowAdd(s => !s)} className="flex items-center gap-2 px-4 py-2 bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.3)] text-[#D4AF37] text-sm rounded-sm hover:bg-[rgba(212,175,55,0.2)] transition-colors">
          <Plus className="w-4 h-4" />Add User
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleCreate} className="bg-[#111111] border border-[rgba(212,175,55,0.3)] rounded-sm p-5 space-y-4">
          <h2 className="text-sm font-semibold text-white">New Admin User</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Username</label>
              <input value={newUser.username} onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} required className={inputClass} placeholder="username" />
            </div>
            <div>
              <label className={labelClass}>Password</label>
              <input type="password" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} required minLength={6} className={inputClass} placeholder="min 6 characters" />
            </div>
            <div>
              <label className={labelClass}>Role</label>
              <select value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))} className={inputClass}>
                <option value="editor">Editor (content only)</option>
                <option value="superadmin">Superadmin (full access)</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="px-4 py-2 bg-[#D4AF37] text-black font-semibold text-sm rounded-sm hover:bg-[#C5A028] transition-colors">Create User</button>
            <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 text-white/50 hover:text-white text-sm transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {changePwd && (
        <form onSubmit={handleChangePassword} className="bg-[#111111] border border-[rgba(212,175,55,0.3)] rounded-sm p-5 space-y-4">
          <h2 className="text-sm font-semibold text-white">Change Password — <span className="text-[#D4AF37]">{changePwd.username}</span></h2>
          <div className="flex gap-3">
            <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} required minLength={6} className={`${inputClass} max-w-xs`} placeholder="New password (min 6 chars)" />
            <button type="submit" className="px-4 py-2 bg-[#D4AF37] text-black font-semibold text-sm rounded-sm hover:bg-[#C5A028] transition-colors">Set Password</button>
            <button type="button" onClick={() => { setChangePwd(null); setNewPwd(''); }} className="px-4 py-2 text-white/50 hover:text-white text-sm transition-colors">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm overflow-hidden">
        {loading && <p className="text-white/40 text-sm text-center py-8">Loading...</p>}
        {!loading && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[rgba(212,175,55,0.1)]">
                <th className="text-left px-4 py-3 text-white/40 text-xs uppercase tracking-wider font-medium">User</th>
                <th className="text-left px-4 py-3 text-white/40 text-xs uppercase tracking-wider font-medium">Role</th>
                <th className="text-left px-4 py-3 text-white/40 text-xs uppercase tracking-wider font-medium hidden sm:table-cell">Last Login</th>
                <th className="text-left px-4 py-3 text-white/40 text-xs uppercase tracking-wider font-medium hidden md:table-cell">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(212,175,55,0.08)]">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[rgba(212,175,55,0.1)] flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-[#D4AF37]" />
                      </div>
                      <span className="text-white">{user.username}</span>
                      {user.username === currentUser?.username && <span className="text-[10px] text-[#D4AF37] bg-[rgba(212,175,55,0.1)] px-1.5 py-0.5 rounded">You</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${user.role === 'superadmin' ? 'bg-[rgba(212,175,55,0.15)] text-[#D4AF37]' : 'bg-white/5 text-white/50'}`}>{user.role}</span>
                  </td>
                  <td className="px-4 py-3 text-white/40 text-xs hidden sm:table-cell">{formatDate(user.last_login)}</td>
                  <td className="px-4 py-3 text-white/40 text-xs hidden md:table-cell">{formatDate(user.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => { setChangePwd(user); setNewPwd(''); }} className="w-7 h-7 flex items-center justify-center text-white/30 hover:text-[#D4AF37] transition-colors" title="Change password"><Key className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(user)} disabled={user.username === currentUser?.username} className="w-7 h-7 flex items-center justify-center text-white/30 hover:text-red-400 disabled:opacity-20 transition-colors" title="Delete user"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default UsersPage;
